// Unpublished Work © 2020-2026 Deere & Company.

import React from 'react';
import {setUserData} from '../action-creators/user-data';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {LoadingContainer} from '../views/common/loading-indicator/loading-container';
import {PageStatus} from '../state/struct/page-status';
import {useOktaAuth} from '@okta/okta-react';
import {useHistory, useLocation} from 'react-router-dom';
import {CustomerRoutes} from '../literals/customer-routes';
import {REDIRECT_USER_TO_PROFILE_SCREEN, REDIRECT_USER_TO_SEARCH_SCREEN, SET_CONFIGURATION} from '../action-list';
import {useIntl} from 'react-intl';
import {logUserEntry} from '../repositories/utils/log-user-entry';

const useUserEntrySession = (applicationName, userId) => {
    const loggedRef = React.useRef(false);

    React.useEffect(() => {
        if (!userId || loggedRef.current) {
            return;
        }

        logUserEntry(applicationName, userId);
        loggedRef.current = true;
    }, [applicationName, userId]);
};

const getInitialPageStatus = (pathname) =>
    pathname === CustomerRoutes.CHECK_HEALTH ?
        PageStatus.meta.map.COMPLETE :
        PageStatus.meta.map.LOADING;

// eslint-disable-next-line max-statements
export const UserDataProvider = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const intl = useIntl();

    const businessActivities = useSelector((state) => state.configuration.businessActivities);
    const applicationName = useSelector((state) => state.configuration.parentAppName);
    const userId = useSelector((state) => state.user?.loggedInUserId);
    const {authState} = useOktaAuth();

    const isCheckHealthPage = location.pathname === CustomerRoutes.CHECK_HEALTH;

    const [pageStatus, setPageStatus] = React.useState(getInitialPageStatus(location.pathname));
    const [errorMessage, setErrorMessage] = React.useState('');
    const routeRequested = useSelector((state) => state.configuration.routing.view);
    const returnLabel = intl.formatMessage({
        id: 'labels.BACK'
    });
    const isUserAccessingEditPageWithoutAccess = (routeRequested === 'EDIT_CUSTOMER' || routeRequested === 'ADD_BUSINESS_CONTACT') && businessActivities.canAddEdit === false;

    const checkIfUserIsAccessingEditWithoutPermissions = () => {
        if (isUserAccessingEditPageWithoutAccess) {
            history.push(CustomerRoutes.CUSTOMER_PROFILE);
            dispatch({
                configuration: {
                    features: {
                        search: {
                            addToContextButton: {
                                show: true,
                                tooltipLabel: returnLabel,
                                label: returnLabel
                            }
                        }
                    },
                    routing: {
                        view: 'CUSTOMER_PROFILE'
                    }
                },
                type: SET_CONFIGURATION
            });
            dispatch({
                isUserRedirectedToProfile: true,
                type: REDIRECT_USER_TO_PROFILE_SCREEN
            });
        }
    };

    const checkIfUserIsAccessingAddPageWithoutPermissions = () => {
        if (routeRequested === 'ADD_CUSTOMER' && businessActivities.canAddEdit === false) {
            history.push(CustomerRoutes.CUSTOMER_SEARCH);
            dispatch({
                configuration: {
                    routing: {
                        view: 'CUSTOMER_SEARCH'
                    }
                },
                type: SET_CONFIGURATION
            });
            dispatch({
                isUserRedirectedToSearch: true,
                type: REDIRECT_USER_TO_SEARCH_SCREEN
            });
        }
    };

    async function loadUser() {
        try {
            await dispatch(setUserData(businessActivities, authState.accessToken));

            setPageStatus(PageStatus.meta.map.COMPLETE);
        } catch (e) {
            setPageStatus(PageStatus.meta.map.ERROR);
            setErrorMessage(e.errorUserMessage);
        }
    }

    React.useEffect(() => {
        if (!isCheckHealthPage) {
            setPageStatus(PageStatus.meta.map.LOADING);
            loadUser();
        } else {
            setPageStatus(PageStatus.meta.map.COMPLETE);
        }
    }, [authState.accessToken, isCheckHealthPage]);

    React.useEffect(() => {
        checkIfUserIsAccessingEditWithoutPermissions();
        checkIfUserIsAccessingAddPageWithoutPermissions();
    }, [businessActivities.canAddEdit]);

    useUserEntrySession(applicationName, userId);

    return (
        <LoadingContainer
            loadingStatus={pageStatus}
            message={errorMessage}
        >
            {props.children}
        </LoadingContainer>
    );
};

UserDataProvider.propTypes = {
    children: PropTypes.node
};
